import React, { useEffect, useRef, useState } from 'react'
import { Alert, Box, IconButton, Tab, Tabs, TextField, Tooltip, Typography } from '@mui/material'
import { Field } from 'react-final-form'
import { useDropzone } from 'react-dropzone'

import UploadIcon from '@mui/icons-material/Upload'
import CodeIcon from '@mui/icons-material/Code'
import VisibilityIcon from '@mui/icons-material/Visibility'

import {
  DEFAULT_DARK_MESSAGE_TEXT_COLOR,
  DEFAULT_DARK_TITLE_TEXT_COLOR,
  DEFAULT_LIGHT_THEME_CARD_BACKGROUND
} from '../../../../../common/constants/themeConstants'
import './htmlEditor.css'

export default function HtmlEditor({ name, dataError, handleDataChange, MAX_DATA_LENGTH, isCurrentThemeLight }) {
  const [tabValue, setTabValue] = useState(0)
  const [previewMode, setPreviewMode] = useState(false)
  const [uploadSuccess, setUploadSuccess] = useState(false)
  const [preservedStyles, setPreservedStyles] = useState('')

  const fieldInputRef = useRef(null)

  const extractStyles = html => {
    if (!html) return ''

    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = html

    let styles = ''

    tempDiv.querySelectorAll('style').forEach(styleTag => {
      if (styleTag.innerHTML.trim()) {
        styles += styleTag.innerHTML + '\n'
      }
    })

    return styles.trim()
  }

  const injectStylesGlobally = () => {
    if (!preservedStyles) return

    const existing = document.querySelectorAll('style[data-html-editor="true"]')
    existing.forEach(style => style.remove())

    const style = document.createElement('style')
    style.setAttribute('data-html-editor', 'true')

    style.innerHTML = `
      ${preservedStyles}
    `

    document.head.appendChild(style)
    console.log('Styles injected globally for contentEditable')
  }

  useEffect(() => {
    return () => {
      const existingGlobalStyles = document.querySelectorAll('style[data-html-editor="true"]')
      existingGlobalStyles.forEach(style => style.remove())
    }
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'text/html': ['.html', '.htm']
    },
    maxSize: 10 * 1024 * 1024,
    onDrop: acceptedFiles => {
      const file = acceptedFiles[0]
      if (file) {
        const reader = new FileReader()
        reader.onload = () => {
          try {
            const htmlContent = reader.result

            const cleanHtml = sanitizeHtml(htmlContent)

            const processedHtml = processImportedHtml(cleanHtml)

            if (fieldInputRef.current) {
              const styles = extractStyles(processedHtml)
              if (styles) {
                setPreservedStyles(styles)
              }

              fieldInputRef.current.onChange(processedHtml)
              handleDataChange(fieldInputRef.current, processedHtml)

              setUploadSuccess(true)
              setTimeout(() => setUploadSuccess(false), 3000)
            }
          } catch (error) {
            console.error('Error processing HTML file:', error)
            alert('Error processing HTML file. Please check the file format and try again.')
          }
        }
        reader.onerror = () => {
          alert('Error reading file. Please try again.')
        }
        reader.readAsText(file)
      }
    },
    onDropRejected: rejectedFiles => {
      const file = rejectedFiles[0]
      if (file && file.errors) {
        const error = file.errors[0]
        if (error.code === 'file-too-large') {
          alert('File is too large. Please select a file smaller than 10MB.')
        } else if (error.code === 'file-invalid-type') {
          alert('Invalid file type. Please select an HTML file (.html or .htm).')
        } else {
          alert('Error uploading file. Please try again.')
        }
      }
    }
  })

  const sanitizeHtml = html => {
    return html
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')

      .replace(/on\w+\s*=\s*["'][^"']*["']/gi, '')
      .replace(/javascript:/gi, '')
      .replace(/vbscript:/gi, '')

      .replace(/<(\/?)b>/gi, '<$1strong>')
      .replace(/<(\/?)i>/gi, '<$1em>')
      .replace(/<(\/?)u>/gi, '<$1u>')
      .replace(/<(\/?)s>/gi, '<$1strike>')
  }

  const processImportedHtml = html => {
    if (!html) return ''

    const styles = extractStyles(html)
    if (styles) {
      setPreservedStyles(styles)
    }

    return html
  }

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue)
  }

  const styles = getStyles(isCurrentThemeLight, isDragActive)

  return (
    <Box sx={styles.container}>
      <Box sx={styles.header}>
        <Typography sx={styles.title}>HTML Content Editor</Typography>
        <Tooltip title={previewMode ? 'Edit Mode' : 'Preview Mode'}>
          <IconButton onClick={() => setPreviewMode(!previewMode)} sx={styles.previewButton}>
            {previewMode ? <CodeIcon /> : <VisibilityIcon />}
          </IconButton>
        </Tooltip>
      </Box>

      {dataError && (
        <Alert severity='error' sx={styles.errorAlert}>
          {dataError}
        </Alert>
      )}

      {uploadSuccess && (
        <Alert severity='success' sx={styles.successAlert}>
          HTML file uploaded successfully!
        </Alert>
      )}

      {previewMode ? (
        <Field name={`${name}.data`}>
          {({ input }) => {
            useEffect(() => {
              if (preservedStyles && input.value) {
                injectStylesGlobally()
              }
            }, [input.value, preservedStyles])

            return (
              <Box sx={styles.previewContainer}>
                <div
                  className='preview-container'
                  data-theme={isCurrentThemeLight ? 'light' : 'dark'}
                  dangerouslySetInnerHTML={{
                    __html:
                      input.value ||
                      '<p style="color: #666; text-align: center; padding: 20px;">No HTML content to preview</p>'
                  }}
                  style={{
                    color: 'inherit',
                    fontFamily: 'inherit',
                    fontSize: 'inherit',
                    backgroundColor: 'transparent',
                    border: 'none',
                    width: '100%',
                    height: '100%',
                    overflow: 'auto',
                    all: 'unset',
                    display: 'block'
                  }}
                />
              </Box>
            )
          }}
        </Field>
      ) : (
        <>
          <Tabs value={tabValue} onChange={handleTabChange} sx={styles.tabs}>
            <Tab label='Code Editor' sx={styles.tab} />
            <Tab label='Upload File' sx={styles.tab} />
          </Tabs>

          <Box sx={styles.content}>
            {tabValue === 0 ? (
              <Field name={`${name}.data`}>
                {({ input }) => {
                  fieldInputRef.current = input
                  return (
                    <TextField
                      {...input}
                      multiline
                      rows={12}
                      fullWidth
                      placeholder='Enter your HTML code here...'
                      onChange={e => {
                        const value = e.target.value
                        const styles = extractStyles(value)
                        if (styles) {
                          setPreservedStyles(styles)
                        }
                        handleDataChange(input, value)
                      }}
                      sx={styles.textField}
                      inputProps={{
                        maxLength: MAX_DATA_LENGTH,
                        style: { fontFamily: 'monospace', fontSize: '14px', lineHeight: '1.5' }
                      }}
                    />
                  )
                }}
              </Field>
            ) : tabValue === 1 ? (
              <Field name={`${name}.data`}>
                {({ input }) => {
                  fieldInputRef.current = input
                  return (
                    <Box {...getRootProps()} sx={styles.dropzone}>
                      <input {...getInputProps()} />
                      <UploadIcon sx={styles.uploadIcon} />
                      <Typography sx={styles.dropzoneText}>
                        {isDragActive
                          ? 'Drop the HTML file here'
                          : 'Drag and drop your HTML file here, or click to browse'}
                      </Typography>
                      <Typography sx={styles.dropzoneHint}>Supported formats: .html, .htm</Typography>
                    </Box>
                  )
                }}
              </Field>
            ) : null}
          </Box>

          <Field name={`${name}.data`}>
            {({ input }) => (
              <Box sx={styles.footer}>
                <Typography sx={styles.characterCount}>
                  {`${(input.value || '').length}/${MAX_DATA_LENGTH} characters`}
                </Typography>
              </Box>
            )}
          </Field>
        </>
      )}
    </Box>
  )
}

const getStyles = (isCurrentThemeLight, isDragActive) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    padding: '24px',
    height: '100%'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  title: {
    fontSize: '24px',
    fontWeight: 600,
    fontFamily: 'Plus Jakarta Sans',
    color: isCurrentThemeLight ? DEFAULT_DARK_TITLE_TEXT_COLOR : 'rgba(255, 255, 255, 1)'
  },
  previewButton: {
    color: isCurrentThemeLight ? '#6123FF' : 'rgba(255, 255, 255, 0.8)',
    backgroundColor: isCurrentThemeLight ? 'rgba(97, 35, 255, 0.1)' : 'rgba(255, 255, 255, 0.1)',
    '&:hover': {
      backgroundColor: isCurrentThemeLight ? 'rgba(97, 35, 255, 0.2)' : 'rgba(255, 255, 255, 0.2)'
    }
  },
  errorAlert: {
    borderRadius: '8px',
    color: isCurrentThemeLight ? DEFAULT_DARK_MESSAGE_TEXT_COLOR : 'rgba(255, 255, 255, 1)',
    backgroundColor: 'rgba(244, 67, 54, 0.15)'
  },
  successAlert: {
    borderRadius: '8px',
    color: isCurrentThemeLight ? DEFAULT_DARK_MESSAGE_TEXT_COLOR : 'rgba(255, 255, 255, 1)',
    backgroundColor: 'rgba(76, 175, 80, 0.15)'
  },
  tabs: {
    '& .MuiTabs-indicator': {
      backgroundColor: isCurrentThemeLight ? '#6123FF' : 'rgba(255, 255, 255, 0.8)'
    }
  },
  tab: {
    fontFamily: 'Plus Jakarta Sans',
    fontWeight: 500,
    color: isCurrentThemeLight ? DEFAULT_DARK_MESSAGE_TEXT_COLOR : 'rgba(255, 255, 255, 0.8)',
    '&.Mui-selected': {
      color: isCurrentThemeLight ? '#6123FF' : 'rgba(255, 255, 255, 1)'
    }
  },
  content: {
    flex: 1,
    minHeight: '300px'
  },
  richEditorContainer: {
    display: 'flex',
    flexDirection: 'column',
    border: isCurrentThemeLight ? '1px solid rgba(0, 0, 0, 0.1)' : '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: '12px',
    overflow: 'hidden',
    background: 'transparent'
  },
  toolbar: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: 'transparent',
    borderBottom: isCurrentThemeLight ? '1px solid rgba(0, 0, 0, 0.1)' : '1px solid rgba(255, 255, 255, 0.1)',
    padding: '12px'
  },
  toolbarRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
    '&:last-child': {
      marginBottom: 0
    }
  },
  buttonGroup: {
    '& .MuiButtonGroup-grouped': {
      border: 'none',
      '&:not(:last-of-type)': {
        borderRight: 'none'
      }
    }
  },
  toolbarButton: {
    color: isCurrentThemeLight ? '#555' : 'rgba(255, 255, 255, 0.8)',
    borderColor: isCurrentThemeLight ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.2)',
    minWidth: '36px',
    height: '36px',
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: isCurrentThemeLight ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.1)',
      borderColor: isCurrentThemeLight ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.3)'
    }
  },
  activeButton: {
    backgroundColor: isCurrentThemeLight ? '#6123FF' : 'rgba(97, 35, 255, 0.3)',
    color: '#fff',
    '&:hover': {
      backgroundColor: isCurrentThemeLight ? '#5018E6' : 'rgba(97, 35, 255, 0.4)'
    }
  },
  fontButton: {
    color: isCurrentThemeLight ? '#555' : 'rgba(255, 255, 255, 0.8)',
    borderColor: isCurrentThemeLight ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.2)',
    backgroundColor: 'transparent',
    minWidth: '120px',
    height: '36px',
    justifyContent: 'flex-start',
    textTransform: 'none',
    fontSize: '14px',
    '&:hover': {
      backgroundColor: isCurrentThemeLight ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.1)',
      borderColor: isCurrentThemeLight ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.3)'
    }
  },
  sizeButton: {
    color: isCurrentThemeLight ? '#555' : 'rgba(255, 255, 255, 0.8)',
    borderColor: isCurrentThemeLight ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.2)',
    backgroundColor: 'transparent',
    minWidth: '60px',
    height: '36px',
    textTransform: 'none',
    fontSize: '14px',
    '&:hover': {
      backgroundColor: isCurrentThemeLight ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.1)',
      borderColor: isCurrentThemeLight ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.3)'
    }
  },
  headerButton: {
    color: isCurrentThemeLight ? '#555' : 'rgba(255, 255, 255, 0.9)',
    borderColor: isCurrentThemeLight ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.15)',
    backgroundColor: isCurrentThemeLight ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.2)',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    minWidth: '40px',
    height: '36px',
    textTransform: 'none',
    fontWeight: 'bold',
    '&:hover': {
      backgroundColor: isCurrentThemeLight ? 'rgba(255, 255, 255, 0.8)' : 'rgba(255, 255, 255, 0.1)',
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
    }
  },
  colorButton: {
    color: isCurrentThemeLight ? '#555' : 'rgba(255, 255, 255, 0.9)',
    borderColor: isCurrentThemeLight ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.15)',
    backgroundColor: isCurrentThemeLight ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.2)',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    minWidth: '36px',
    height: '36px',
    position: 'relative',
    '&:hover': {
      backgroundColor: isCurrentThemeLight ? 'rgba(255, 255, 255, 0.8)' : 'rgba(255, 255, 255, 0.1)',
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
    }
  },
  colorIndicator: {
    position: 'absolute',
    bottom: '2px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '20px',
    height: '3px',
    borderRadius: '1px'
  },
  divider: {
    height: '20px',
    margin: '0 4px',
    borderColor: isCurrentThemeLight ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.2)'
  },

  fontSelectorContainer: {
    maxHeight: '300px',
    width: '200px',
    overflow: 'auto'
  },
  fontOption: {
    padding: '12px 16px',
    cursor: 'pointer',
    fontSize: '14px',
    borderBottom: isCurrentThemeLight ? '1px solid rgba(0, 0, 0, 0.05)' : '1px solid rgba(255, 255, 255, 0.1)',
    '&:hover': {
      backgroundColor: isCurrentThemeLight ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.1)'
    }
  },
  sizeSelectorContainer: {
    maxHeight: '300px',
    width: '100px',
    overflow: 'auto'
  },
  sizeOption: {
    padding: '8px 16px',
    cursor: 'pointer',
    textAlign: 'center',
    borderBottom: isCurrentThemeLight ? '1px solid rgba(0, 0, 0, 0.05)' : '1px solid rgba(255, 255, 255, 0.1)',
    '&:hover': {
      backgroundColor: isCurrentThemeLight ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.1)'
    }
  },
  selectedOption: {
    backgroundColor: isCurrentThemeLight ? '#6123FF' : 'rgba(97, 35, 255, 0.3)',
    color: '#fff'
  },
  colorPickerContainer: {
    padding: '16px',
    width: '280px'
  },
  colorPickerTitle: {
    marginBottom: '8px',
    fontWeight: 600,
    color: isCurrentThemeLight ? '#333' : '#fff'
  },
  colorGrid: {
    marginBottom: '16px'
  },
  colorBox: {
    width: '24px',
    height: '24px',
    cursor: 'pointer',
    border: '1px solid rgba(0, 0, 0, 0.1)',
    borderRadius: '4px',
    '&:hover': {
      transform: 'scale(1.1)'
    }
  },
  customColorRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  colorInput: {
    width: '40px',
    height: '32px',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer'
  },
  applyColorButton: {
    textTransform: 'none'
  },
  textField: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: isCurrentThemeLight ? DEFAULT_LIGHT_THEME_CARD_BACKGROUND : 'rgba(255, 255, 255, 0.1)',
      border: isCurrentThemeLight ? '1px solid rgba(0, 0, 0, 0.15)' : '1px solid rgba(255, 255, 255, 0.2)',
      borderRadius: '12px'
    },
    '& .MuiInputBase-input': {
      color: isCurrentThemeLight ? DEFAULT_DARK_MESSAGE_TEXT_COLOR : 'rgba(255, 255, 255, 0.9)'
    }
  },
  dropzone: {
    border: '2px dashed',
    borderColor: isCurrentThemeLight ? '#DBDBE6' : 'rgba(255, 255, 255, 0.3)',
    borderRadius: '12px',
    padding: '40px',
    textAlign: 'center',
    cursor: 'pointer',
    backgroundColor: isCurrentThemeLight
      ? isDragActive
        ? 'rgba(97, 35, 255, 0.05)'
        : 'transparent'
      : isDragActive
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(255, 255, 255, 0.05)'
  },
  uploadIcon: {
    fontSize: '48px',
    color: isCurrentThemeLight ? '#6123FF' : 'rgba(255, 255, 255, 0.6)',
    marginBottom: '16px'
  },
  dropzoneText: {
    fontFamily: 'Plus Jakarta Sans',
    fontSize: '16px',
    fontWeight: 500,
    color: isCurrentThemeLight ? DEFAULT_DARK_MESSAGE_TEXT_COLOR : 'rgba(255, 255, 255, 0.9)',
    marginBottom: '8px'
  },
  dropzoneHint: {
    fontFamily: 'Plus Jakarta Sans',
    fontSize: '14px',
    color: isCurrentThemeLight ? '#76767D' : 'rgba(255, 255, 255, 0.6)'
  },
  previewContainer: {
    flex: 1,
    padding: '20px',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: '12px',
    overflow: 'auto',
    minHeight: '400px'
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end'
  },
  characterCount: {
    fontFamily: 'Plus Jakarta Sans',
    fontSize: '12px',
    color: isCurrentThemeLight ? '#76767D' : 'rgba(255, 255, 255, 0.6)'
  }
})
